/**
 * AdminExpenseModal — Modale admin pour créer une dépense immeuble depuis PMS.
 * Extrait de DocsAndExpensesPanel.jsx (Batch #14.221).
 */
import { TPS_RATE, TVQ_RATE } from "@/lib/taxes";
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Wallet, X, Building2, CheckCircle2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "../_shared";
import Field from "../_Field";

const EXPENSE_CATEGORIES = [
  { value: "entretien_reparation",    label: "Entretien / Réparation" },
  { value: "renovations",              label: "Rénovations" },
  { value: "taxes_municipales",        label: "Taxes municipales" },
  { value: "taxes_scolaires",          label: "Taxes scolaires" },
  { value: "assurance_immeuble",       label: "Assurance immeuble" },
  { value: "assurance_responsabilite", label: "Assurance responsabilité" },
  { value: "energie_chauffage",        label: "Énergie / Chauffage" },
  { value: "electricite_communes",     label: "Électricité communes" },
  { value: "eau_aqueduc",              label: "Eau / Aqueduc" },
  { value: "internet_telecom",         label: "Internet / Télécom" },
  { value: "concierge_menage",         label: "Concierge / Ménage" },
  { value: "deneigement_paysagement",  label: "Déneigement / Paysagement" },
  { value: "extermination",            label: "Extermination" },
  { value: "frais_juridiques",         label: "Frais juridiques" },
  { value: "frais_comptables",         label: "Frais comptables" },
  { value: "frais_administration",     label: "Frais d'administration" },
  { value: "frais_bancaires_stripe",   label: "Frais bancaires / Stripe" },
  { value: "interets_hypotheque",      label: "Intérêts hypothèque" },
  { value: "publicite_marketing",      label: "Publicité / Marketing" },
  { value: "petits_outils",            label: "Petits outils" },
  { value: "fournitures_bureau",       label: "Fournitures bureau" },
  { value: "autre",                    label: "Autre" },
];


function AdminExpenseModal({ onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    master_id: "",
    building_id: "",
    unit_id: "",
    vendor: "",
    vendor_address: "",
    vendor_phone: "",
    amount_gross: "",
    tax_type: "tps_tvq",
    tps_amount: "",
    tvq_amount: "",
    amount_total: "",
    invoice_date: today,
    invoice_number: "",
    category: "entretien_reparation",
    internal_notes: "",
  });

  const [masters, setMasters] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/clients?limit=200`, { headers: authH() });
        setMasters(r.data?.items || r.data?.clients || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (!form.master_id) { setBuildings([]); setUnits([]); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/pms/master/${form.master_id}/properties`, { headers: authH() });
        setBuildings(r.data?.items || r.data?.properties || []);
      } catch { setBuildings([]); }
    })();
  }, [form.master_id]);

  useEffect(() => {
    if (!form.building_id) { setUnits([]); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/pms/property/${form.building_id}/units`, { headers: authH() });
        setUnits(r.data?.items || r.data?.units || []);
      } catch { setUnits([]); }
    })();
  }, [form.building_id]);

  useEffect(() => {
    const g = parseFloat(form.amount_gross) || 0;
    const tps = parseFloat(form.tps_amount) || 0;
    const tvq = parseFloat(form.tvq_amount) || 0;
    if (g > 0) {
      setForm((f) => ({ ...f, amount_total: (g + tps + tvq).toFixed(2) }));
    }
  }, [form.amount_gross, form.tps_amount, form.tvq_amount]);

  const computeTaxes = () => {
    const g = parseFloat(form.amount_gross) || 0;
    if (!g) return;
    setForm((f) => ({
      ...f,
      tax_type: "tps_tvq",
      tps_amount: (g * TPS_RATE).toFixed(2),
      tvq_amount: (g * TVQ_RATE).toFixed(2),
    }));
  };

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const submit = async () => {
    if (!form.master_id) return toast.error("Sélectionnez un client master");
    if (!form.building_id) return toast.error("Sélectionnez un immeuble");
    if (!form.amount_gross || parseFloat(form.amount_gross) <= 0) return toast.error("Montant brut requis");
    if (!form.invoice_date) return toast.error("Date facture requise");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/pms/expenses`, {
        ...form,
        amount_gross: parseFloat(form.amount_gross),
        tps_amount: parseFloat(form.tps_amount) || 0,
        tvq_amount: parseFloat(form.tvq_amount) || 0,
        amount_total: parseFloat(form.amount_total) || (parseFloat(form.amount_gross) + (parseFloat(form.tps_amount) || 0) + (parseFloat(form.tvq_amount) || 0)),
      }, { headers: authH() });
      toast.success("Dépense enregistrée");
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-expense-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-bold text-slate-800 inline-flex items-center gap-2">
            <Wallet className="w-4 h-4 text-rose-600" /> Saisir une dépense
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="admin-expense-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Client master *">
              <select value={form.master_id} onChange={(e) => setForm({ ...form, master_id: e.target.value, building_id: "", unit_id: "" })} className="cc-input" data-testid="admin-expense-master">
                <option value="">— Sélectionner —</option>
                {masters.map((m) => (
                  <option key={m.id} value={m.id}>{m.couple_display_name || m.display_name || m.email || m.id}</option>
                ))}
              </select>
            </Field>
            <Field label="Immeuble *">
              <select value={form.building_id} onChange={(e) => setForm({ ...form, building_id: e.target.value, unit_id: "" })} className="cc-input" disabled={!form.master_id} data-testid="admin-expense-building">
                <option value="">— Sélectionner —</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name || b.address_line}</option>
                ))}
              </select>
            </Field>
            <Field label="Appartement (optionnel)">
              <select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} className="cc-input" disabled={!form.building_id} data-testid="admin-expense-unit">
                <option value="">— Dépense d'immeuble —</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>Apt {u.unit_label}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
            <h4 className="text-[11px] uppercase font-bold text-slate-700 mb-2 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Informations du fournisseur
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom du fournisseur"><input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="cc-input" data-testid="admin-expense-vendor" /></Field>
              <Field label="N° facture"><input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="cc-input" data-testid="admin-expense-invoice-number" /></Field>
              <Field label="Adresse fournisseur"><input value={form.vendor_address} onChange={(e) => setForm({ ...form, vendor_address: e.target.value })} className="cc-input" /></Field>
              <Field label="Téléphone fournisseur"><input value={form.vendor_phone} onChange={(e) => setForm({ ...form, vendor_phone: e.target.value })} className="cc-input" /></Field>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[11px] uppercase font-bold text-slate-700">Montants</h4>
              <button onClick={computeTaxes} className="text-[10px] text-blue-600 hover:underline" data-testid="admin-expense-auto-taxes">
                Calcul auto TPS 5% / TVQ 9.975%
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Date facture *">
                <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="cc-input" data-testid="admin-expense-invoice-date" />
              </Field>
              <Field label="Montant brut $ *">
                <input type="number" step="0.01" value={form.amount_gross} onChange={(e) => setForm({ ...form, amount_gross: e.target.value })} className="cc-input font-mono" data-testid="admin-expense-amount-gross" />
              </Field>
              <Field label="TPS $">
                <input type="number" step="0.01" value={form.tps_amount} onChange={(e) => setForm({ ...form, tps_amount: e.target.value })} className="cc-input font-mono" data-testid="admin-expense-tps" />
              </Field>
              <Field label="TVQ $">
                <input type="number" step="0.01" value={form.tvq_amount} onChange={(e) => setForm({ ...form, tvq_amount: e.target.value })} className="cc-input font-mono" data-testid="admin-expense-tvq" />
              </Field>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <Field label="Type de taxe">
                <select value={form.tax_type} onChange={(e) => setForm({ ...form, tax_type: e.target.value })} className="cc-input">
                  <option value="none">Aucune</option>
                  <option value="tps_only">TPS seulement</option>
                  <option value="tvq_only">TVQ seulement</option>
                  <option value="tps_tvq">TPS + TVQ</option>
                  <option value="combined">Combinée</option>
                  <option value="exempt">Exempt</option>
                </select>
              </Field>
              <Field label="Total facture $ (calculé auto)">
                <input type="number" step="0.01" value={form.amount_total} readOnly className="cc-input font-mono bg-slate-100 text-slate-700 font-bold" data-testid="admin-expense-amount-total" />
              </Field>
            </div>
          </div>

          <Field label="Catégorie comptable *">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="cc-input" data-testid="admin-expense-category">
              {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Notes internes (visibles cabinet uniquement)">
            <textarea value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} rows={2} maxLength={500} className="cc-input" data-testid="admin-expense-notes" />
          </Field>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50 text-sm text-slate-700">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="admin-expense-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Enregistrer la dépense
          </button>
        </div>
      </div>
    </div>
  );
}

export { AdminExpenseModal };
